import React from 'react';
import {Chip, styled} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
} from '@lightricks/react-design-system';
import {PricingCtaButton} from '@contentlab/views/contentlab/pricing/components/pricing-bundle/Buttons';
import DurationToggle from '@contentlab/views/contentlab/pricing/components/pricing-bundle/DurationToggle';
import Features from '@contentlab/views/contentlab/pricing/components/pricing-bundle/Features';
import PlanSelector from '@contentlab/views/contentlab/pricing/components/pricing-bundle/PlanSelector';
import PriceFootnote from '@contentlab/views/contentlab/pricing/components/pricing-bundle/PriceFootnote';
import Price from '@contentlab/views/contentlab/pricing/components/pricing-bundle/PriceSection';
import translate from '@/utils/translate';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';
import Conditional from '@/components/conditional';
import usePricingPlanSelection from '@/hooks/use-pricing-plan-selection/usePricingPlanSelection';
import styles from './MultiPlansPricingBundle.module.scss';

export type MultiPlansPricingBundleProps = {
  plans: SubscriptionPlan[];
  bundle: ContentLabPlanBundle;
  name: string;
  description: string;
  isHighlighted: boolean;
  isActiveBundle: boolean;
  activePlan?: SubscriptionPlan;
  activeSubscriptionId?: string;
  sendUiInteractionEvent: (buttonName: string) => void;
  sendPurchaseProcessStartedEvent: (
    product_id: string,
    process_id?: string
  ) => void;
  isOnLaunch: boolean;
  isTrialFeatureEnabled: boolean;
  onLaunchCallback?: () => void;
  enforcePaymentManagerRole?: boolean;
};

function MultiPlansPricingBundle(props: MultiPlansPricingBundleProps) {
  const {
    plans,
    bundle,
    name,
    description,
    isHighlighted,
    isActiveBundle,
    activePlan,
    activeSubscriptionId,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
    isOnLaunch,
    onLaunchCallback,
    enforcePaymentManagerRole,
    isTrialFeatureEnabled = false,
  } = props;

  const {
    selectedSubscriptionPlan,
    selectedDuration,
    presentedPlanList,
    handleDurationToggled,
    handlePlanSelected,
    handleSubscribeButtonClick,
    getOriginalPrice,
  } = usePricingPlanSelection({
    plans,
    activeSubscriptionId,
    activePlan,
    shouldSkipUpgradeModal: true,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
    isOnLaunch,
    isTrialFeatureEnabled,
    onLaunchCallback,
    enforcePaymentManagerRole,
  });

  const getCtaButtonText = () => {
    if (selectedSubscriptionPlan?.id === activePlan?.id) {
      return 'Current Plan';
    }
    if (isActiveBundle) {
      return 'Update Plan';
    }
    if (isTrialFeatureEnabled) {
      return 'Start Free Trial Now';
    }
    return 'Get Started';
  };

  const LimitedOfferChip = styled(Chip)({
    background: designSystemToken('semantic.bg.brand-secondary'),
    color: designSystemToken('semantic.fg.brand'),
    height: '18px',
    lineHeight: '18px',
    fontSize: '12px',
  });

  return (
    selectedSubscriptionPlan && (
      <div className={styles.container}>
        <div
          className={`${styles.stickyBanner} ${
            isHighlighted ? styles.highlightedBundle : ''
          } ${isActiveBundle ? styles.activeBundle : ''}`}
          style={{
            background: isHighlighted
              ? designSystemToken('semantic.fg.brand')
              : designSystemToken('semantic.bg.secondary'),
            border: isHighlighted
              ? `1px solid ${designSystemToken('semantic.fg.brand')}`
              : 'none',
          }}
        >
          <Conditional condition={isHighlighted}>
            <Headline size="sm" className={styles.highlightTitle}>
              {translate('Best Value - up to 60% off')}
            </Headline>
          </Conditional>
        </div>
        <div
          className={`${styles.pricingBundle} ${
            isActiveBundle ? styles.activeBundle : ''
          } ${isHighlighted ? styles.highlightedBundle : ''}`}
          style={{
            border: isHighlighted
              ? `1px solid ${designSystemToken('semantic.fg.brand')}`
              : 'none',
          }}
        >
          <DurationToggle
            selectedDuration={selectedDuration}
            handleDurationChange={handleDurationToggled}
            isDisabled={
              activePlan !== undefined &&
              activePlan.bundle === ContentLabPlanBundle.PRO
            }
          />
          <div
            className={`${styles.bundleHeader}`}
            style={{
              paddingBottom: isTrialFeatureEnabled ? '0px' : '12px',
            }}
          >
            <Headline size="lg">{name}</Headline>
            <Body size="sm" className={styles.description}>
              {description}
            </Body>
            <Conditional condition={isTrialFeatureEnabled}>
              <LimitedOfferChip
                label="Limited Offer!"
                size="small"
                className={styles.limitedOffer}
              />
            </Conditional>
          </div>
          <div>
            <Price
              bundle={bundle}
              currency={selectedSubscriptionPlan.currency}
              price={selectedSubscriptionPlan.price}
              originalPrice={getOriginalPrice()}
              isTrialFeatureEnabled={isTrialFeatureEnabled}
            />
            <PriceFootnote
              selectedDuration={selectedDuration}
              selectedSubscriptionPlan={selectedSubscriptionPlan}
              className={styles.priceFootnoteText}
              isTrialFeatureEnabled={isTrialFeatureEnabled}
            />
          </div>
          <PlanSelector
            presentedPlanList={presentedPlanList}
            selectedSubscriptionPlan={selectedSubscriptionPlan}
            handlePlanChange={handlePlanSelected}
          />
          <div className={styles.buttons}>
            <PricingCtaButton
              onClick={() => handleSubscribeButtonClick(getCtaButtonText())}
              isHighlighted={isHighlighted}
              isDisabled={selectedSubscriptionPlan.isDisabled}
              isCurrentPlan={selectedSubscriptionPlan.id === activePlan?.id}
              text={translate(getCtaButtonText())}
            />
          </div>
          <Features
            bundle={bundle}
            seatsCount={selectedSubscriptionPlan.editors}
            videosCount={selectedSubscriptionPlan.videos}
          />
        </div>
      </div>
    )
  );
}

export default MultiPlansPricingBundle;
